/**
 * The cascading hierarchy filter — the two-section block at the head of the §F5.8 widget.
 *
 * The client's ask, in their words: *a cascading dropdown on the operational hierarchy that
 * scopes the whole Map Explorer — "affects the numbers and other things as well; all other
 * layers should disappear except the filtered"*. This component is the control; everything it
 * does is one field, `state.scope`, which the choropleth, the overlays, the boundary rows,
 * the legend's classification, the search box, the status bar and the dock popover all read.
 *
 * ## Two branches, not one path — and two headed sections, not a sentence
 *
 * §C has two hierarchies hanging off a country: admin (region → commune) and operational
 * (watershed → partnership). They are not levels of each other — a partnership overlaps
 * communes rather than containing or being contained by them (§E4 area-weights between them
 * through `unit_overlaps`) — so a single five-deep cascade would be a lie about the data.
 * They are rendered as two branches, and **picking in one clears the other**, which falls out
 * of the model rather than being enforced: a scope is *one* unit, so choosing a watershed
 * replaces a region scope and the region select re-derives itself to "All regions".
 *
 * That exclusivity used to be *explained*, in a line of prose under the selects and an "or
 * filter by watershed" divider between them. IFDC's read of the reviewed build was that a
 * control which has to describe its own rule has the wrong shape: the two headings —
 * **Filter by:** (country → region → commune) and **View data by:** (watershed →
 * partnership) — now say which question each branch answers, and the selects re-deriving
 * themselves shows the rest. No behaviour moved with the text (NOTES #400).
 *
 * ## The selects are derived, never stored
 *
 * There is no local `useState` for the four values. Each select reads its value out of
 * `scope` (`scope.parentId` is what lets a commune scope leave its region standing above it),
 * so the control cannot drift from the map: a permalink restore, a "Clear filter" click and a
 * country change that drops an out-of-country scope all land in the same place with no
 * synchronising effect to get wrong.
 *
 * ## Options come from `/hierarchy/options?operational=1`
 *
 * Names, no geometry (§D2). Filling these four selects from `/hierarchy/units` would be
 * 141 kB of commune polygons for a 47-entry list — see `apps/hierarchy/views.py::options`.
 *
 * **Operational only** (client review R6): a unit is listed when it, or something beneath it,
 * carries a located MELS activity or an approved MELS-sourced value. Live that is 16 regions
 * of 82 and 67 communes of 1 048 — the rest are places the programme does not report from,
 * and offering them is offering the reader an empty map. The *map* still draws every
 * boundary; it is the picker that is shortened.
 *
 * **…except when the shortlist would be empty**, which is a stack that has a hierarchy and no
 * MELS sync yet: `/hierarchy/options` then answers with the full list rather than with `[]`,
 * so this component never renders a select a reader cannot use. Nothing here had to change for
 * it — the rows arrive in the same shape either way, which is the point. The server marks the
 * answer with an `X-Operational-Fallback: 1` header for the day this wants to say so out loud;
 * `getJson` does not read headers today and no string was invented for a state no reviewed
 * design has a sentence for.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  fetchUnitOptions,
  queryKeys,
  type UnitOption,
  type UnitType,
} from '../../lib/api';
import { COUNTRY_NAMES, scopeFromUnit, useAppStore, type Country } from '../../lib/state';
import { cascadeSelection, CASCADE_COUNTRIES } from './cascade';

interface LevelSelectProps {
  id: string;
  label: string;
  /** The "no pick at this level" option — `All regions`, `All communes`, … */
  anyLabel: string;
  value: string;
  options: UnitOption[] | undefined;
  disabled?: boolean;
  onPick: (option: UnitOption | null) => void;
}

function LevelSelect({
  id, label, anyLabel, value, options, disabled, onPick,
}: LevelSelectProps) {
  return (
    <label className="bw-fl-row" htmlFor={id}>
      <span className="bw-fl-label">{label}</span>
      <select
        id={id}
        data-cascade={id}
        // A stale `value` — the scope's id before its option list has landed — would make
        // React fall back to the first entry and *look* like "All regions" over a filtered
        // map. Rendering the current pick as an option of its own keeps the two agreeing
        // through the fetch.
        value={value}
        disabled={disabled || !options}
        onChange={(e) =>
          onPick(e.target.value ? (options ?? []).find((o) => o.id === e.target.value) ?? null : null)
        }
      >
        <option value="">{anyLabel}</option>
        {(options ?? []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
        {value && !(options ?? []).some((o) => o.id === value) ? (
          <option value={value}>{value}</option>
        ) : null}
      </select>
    </label>
  );
}

/** One options query, with the §F1 key shape. `enabled` is the caller's gate. */
function useOptions(
  type: UnitType,
  level: number | null,
  country: Country,
  parent: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.unitOptions(type, level, country, parent),
    queryFn: () => fetchUnitOptions(type, level, country, parent),
    enabled,
    // The hierarchy changes when IFDC re-ingests, not while a reader is clicking.
    staleTime: 30 * 60 * 1000,
  }).data;
}

export function CascadeFilter() {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const setCountry = useAppStore((s) => s.setCountry);
  const scope = useAppStore((s) => s.scope);
  const setScope = useAppStore((s) => s.setScope);
  const clearScope = useAppStore((s) => s.clearScope);

  const picked = cascadeSelection(scope);

  // Regions and watersheds are the two branches' first rung and both hang off the country
  // filter — at `ALL` they are the programme's 82 and 11, which is a long list but a live
  // one, and better than a disabled control that gives the reader nowhere to start.
  const regions = useOptions('admin', 1, country, null, true);
  const watersheds = useOptions('watershed', null, country, null, true);
  // The second rung is `?parent=` — the same subtree filter the map is using.
  const communes = useOptions('admin', 2, 'ALL', picked.region || null, Boolean(picked.region));
  const partnerships = useOptions(
    'partnership', null, 'ALL', picked.watershed || null, Boolean(picked.watershed),
  );

  /**
   * Clearing a select means "go back up one rung", not "clear the filter": emptying the
   * Commune box on a commune scope should leave the reader looking at the region they were
   * already inside. `fallback` is that rung — `null` at the **top**, where clearing really is
   * clearing, and where client correction D3 now sends the map all the way back to the first
   * operational view (`clearScope`: no scope, regions, country `ALL`, programme extent).
   *
   * That is the same destination as the "Clear filter" link, deliberately: emptying the
   * Region box *is* clearing the filter, and two controls that mean the same thing must not
   * land in two different places.
   */
  const pick = (option: UnitOption | null, fallback: UnitOption | undefined | null) => {
    if (option) {
      setScope(scopeFromUnit(option));
    } else if (fallback) {
      setScope(scopeFromUnit(fallback));
    } else {
      clearScope();
    }
  };

  const regionOption = (regions ?? []).find((o) => o.id === picked.region) ?? null;
  const watershedOption = (watersheds ?? []).find((o) => o.id === picked.watershed) ?? null;

  return (
    <div className="bw-filter" data-bwfilter="">
      <div className="bw-fl-sec" data-cascadesection="filter">
        <div className="bw-fl-head">
          <span>{t('hierarchy.filter.filterBy')}</span>
          {/*
            One "Clear filter", in the block's top corner: it clears `scope` whichever
            dropdown set it, and it is the only place that works for a country-level scope,
            which no dropdown below shows a pick for.

            D3: it appears for a **country** pick too. The country box is the cascade's first
            rung and `clearScope` now resets it, so a map filtered to Burkina Faso alone is a
            filtered map with something to clear — offering the link only once a region was
            picked left the one state D3 is about with no way out but the country select.
          */}
          {scope || country !== 'ALL' ? (
            <button
              type="button"
              className="bw-fl-clear"
              data-cascadeclear=""
              onClick={clearScope}
              title={t('hierarchy.filter.clearTitle')}
            >
              {t('hierarchy.filter.clear')}
            </button>
          ) : null}
        </div>

        <label className="bw-fl-row" htmlFor="cascadeCountry">
          <span className="bw-fl-label">{t('hierarchy.filter.country')}</span>
          <select
            id="cascadeCountry"
            data-cascade="cascadeCountry"
            value={country}
            onChange={(e) => setCountry(e.target.value as Country)}
          >
            {CASCADE_COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {t(`country.${c}`, COUNTRY_NAMES[c])}
              </option>
            ))}
          </select>
        </label>

        <LevelSelect
          id="cascadeRegion"
          label={t('hierarchy.filter.region')}
          anyLabel={t('hierarchy.filter.allRegions')}
          value={picked.region}
          options={regions}
          onPick={(o) => pick(o, null)}
        />
        <LevelSelect
          id="cascadeCommune"
          label={t('hierarchy.filter.commune')}
          anyLabel={t('hierarchy.filter.allCommunes')}
          value={picked.commune}
          options={communes}
          disabled={!picked.region}
          onPick={(o) => pick(o, regionOption)}
        />
        <LevelSelect
          id="cascadeWatershed"
          label={t('hierarchy.filter.watershed')}
          anyLabel={t('hierarchy.filter.allWatersheds')}
          value={picked.watershed}
          options={watersheds}
          onPick={(o) => pick(o, null)}
        />
        <LevelSelect
          id="cascadePartnership"
          label={t('hierarchy.filter.partnership')}
          anyLabel={t('hierarchy.filter.allPartnerships')}
          value={picked.partnership}
          options={partnerships}
          disabled={!picked.watershed}
          onPick={(o) => pick(o, watershedOption)}
        />
      </div>

      {/* What the filter is doing, only while it is doing something — never a standing rule. */}
      {scope ? (
        <div className="bw-fl-hint" data-cascadehint="">
          {t('hierarchy.filter.scoped', { name: scope.name })}
        </div>
      ) : null}
    </div>
  );
}

export default CascadeFilter;
